import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { PortalMenuClickCustomEvent } from '../../models/events/portalMenuClickCustomEvent';
import { MaterialIconVariant } from '../../models/portalActions';
import store from '../../store/store';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-menu-item',
    styleUrl: 'portal-menu-item.scss',
    shadow: true,
})
export class PortalMenuItem {
    @Element() el: HTMLPortalMenuItemElement;

    /**
    * disabled property for menu item
    * @memberof PortalMenuItem
    */
    @Prop() disabled?: boolean;

    /**
    * Data of clicked menu
    * @memberof PortalMenuItem
    */
    @Prop() data?: object | string;

    /**
     * The icon of the menu item
     * @memberof PortalMenuItem
     */
    @Prop() icon?: string;

    /**
     * The primary text on the menu item
     * @memberof PortalMenuItem
     */

    @Prop() primaryText: string;

    /**
     * The secondary text of the menu item
     * @memberof PortalMenuItem
     */
    @Prop() secondaryText: string;

    /**
     * The group of the menu item to create radio options
     * @memberof PortalMenuItem
     */
    @Prop() group: string;

    /**
     * The menu id of the menu item for radio options
     * @memberof PortalMenuItem
     */
    @Prop() menuid: string;

    /**
     * set the primary text as the header of the menu item
     * @memberof PortalMenuItem
     */
    @Prop({ reflect: true }) header: boolean = false;

    /**
     * set the menu item with a line at the bottom as a breaker
     * @memberof PortalMenuItem
     */
    @Prop({ reflect: true }) breakbottom: boolean = false;

    /**
     * set the menu item with a line at the top as a breaker
     * @memberof PortalMenuItem
     */
    @Prop({ reflect: true }) breaktop: boolean = false;

    /**
     * set an activated state for the menu item
     * @memberof PortalMenuItem
     */
    @Prop({ reflect: true }) activated: boolean = false;

    /**
     * change the menu item to be non interactive changing the cursor to none
     * @memberof PortalMenuItem
     */
    @Prop({ reflect: true }) noninteractive: boolean = false;

    /**
     * Theme override to bypass store state
     * @memberof PortalMenuItem
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Variation of the Icon
     * @memberof PortalMenuItem
     */
    @Prop() variant: MaterialIconVariant = 'outlined';

    /**
     * Type of menu item
     * @memberof PortalMenuItem
     */
    @Prop() appearance?: 'primary' | 'error';

    /**
     * The aria-role for the menu item
     * @memberof PortalMenuItem
     */
    @Prop() itemRole?: string;

    /**
    * Triggered when menu item area is clicked
    */
    @Event({
        eventName: 'portalMenuItemClick',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) portalMenuItemClick: EventEmitter<PortalMenuClickCustomEvent>;

    render() {
        return (
            <div class={getTheme(store, this.theme)} {...(this.itemRole ? { role: this.itemRole } : {})}>
                <button
                    id={this.menuid}
                    disabled={this.disabled}
                    data-cy={'portal-menu-item-' + this.menuid}
                    aria-disabled={this.disabled}
                    tabindex={this.el.tabIndex || '-1'}
                    class={`
                        ${this.disabled ? 'disabled' : ''}
                        ${this.breakbottom ? 'break-bottom' : ''}
                        ${this.breaktop ? 'break-top' : ''}
                        ${this.appearance ? this.appearance : ''}
                        menu-item-base`}
                    onClick={this.onClickHandler}
                    style={
                        {
                            'min-height': `${this.secondaryText ? '64px' : '32px'}`,
                            '--cursor': `${!this.noninteractive ? 'pointer' : 'cursor'}`,

                        }}>

                    {this.icon &&
                        <div class="menu-item-icon">
                            <portal-icon variant={this.variant} name={this.icon} size="20px"></portal-icon>
                        </div>
                    }

                    <div class="menu-text-group">
                        <div id="primaryText" class="menu-text" style={
                            {
                                'font-weight': `${this.secondaryText || this.header || this.appearance === 'error' ? '600' : 'normal'}`,
                                '--cursor': `${!this.noninteractive ? 'pointer' : 'cursor'}`,
                            }}>{this.primaryText}</div>

                        <div id="secondaryText" class="menu-text">{this.secondaryText}</div>
                    </div>
                </button>
            </div>
        );
    }

    private onClickHandler = (event) => {
        this.portalMenuItemClick.emit(
            {
                id: this.menuid,
                group: this.group,
                event: event,
                data: this.data,
            },
        );
    };
}

